/**
 * Terrain Narrative Manager
 * 
 * Manages terrain-based exploration narratives (not tied to coordinates).
 * Mirrors the quest system architecture for consistency.
 */

import { getFirestore, collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AxialCoord } from '../types/terrain';
import { getPlayerNarrativeForHex, recordPlayerNarrative, getPlayerSeenContentIds, clearSeenNarratives } from '../services/playerNarrativeTracking';
import { FEATURES } from '../config/features';

const db = getFirestore();
const functions = getFunctions();

// Maximum number of narratives to generate per terrain type
// Once this cap is reached, player cycles through existing narratives
const TERRAIN_NARRATIVE_CAP = 30;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface TerrainNarrative {
  contentId: string;
  storyText: string;
  audioUrl: string | null;
  hasMonster: boolean;
  monsterName: string | null;
  terrainType: string; // e.g., "Forest", "Desert", "Mountain"
  terrain: number; // Terrain type enum value
  faction: string | null;
  metadata?: {
    createdAt: any;
    generatedBy: string;
    usedByPlayers?: number;
    tags?: string[];
  };
}

export interface NarrativeResult {
  narrative: TerrainNarrative;
  wasGenerated: boolean; // True if newly generated
  cached: boolean; // True if loaded from cache
  isRevisit: boolean; // True if player has been to this hex before
}

// ============================================================================
// MAIN FUNCTION
// ============================================================================

/**
 * Get or generate terrain narrative for a hex
 * 
 * Flow:
 * 1. Check if player has visited this hex before (consistent revisit)
 * 2. If revisit: return same narrative they saw before
 * 3. If first visit: query available terrain narratives (exclude seen ones)
 * 4. If no available narratives: generate new one
 * 5. Record narrative assignment for this player+hex
 */
export async function getOrGenerateTerrainNarrative(
  terrainType: string,
  terrain: number,
  hexCoord: AxialCoord,
  faction: string | null,
  userId: string,
  worldTheme: string = 'brynlore',
  requestMonster: boolean = false,
  monsterName: string | null = null,
  previousHex?: AxialCoord,
  previousTerrain?: number,
  recordSeenNarrativeCallback?: (hexKey: string, contentId: string) => void,
  seenNarratives?: Array<{ hexKey: string; contentId: string }>
): Promise<NarrativeResult> {
  try {
    const hexKey = `${hexCoord.q},${hexCoord.r}`;
    
    console.log(`\n🗺️  ============ GETTING TERRAIN NARRATIVE ============`);
    console.log(`   Hex: ${hexKey}`);
    console.log(`   Terrain: ${terrainType} (${terrain})`);
    console.log(`   User ID: ${userId}`);
    console.log(`   Faction: ${faction || 'wilderness'}`);
    
    // STEP 1: Check if player has been to this hex before
    const existingContentId = await getPlayerNarrativeForHex(userId, hexKey);
    
    if (existingContentId) {
      console.log(`\n🔄 REVISIT: Player has been here before`);
      console.log(`   Loading their previous narrative: ${existingContentId}`);
      
      // Fetch the same narrative they saw before
      const contentDoc = await getDoc(doc(db, 'storyContent', existingContentId));
      
      if (contentDoc.exists()) {
        const content = contentDoc.data();
        
        return {
          narrative: {
            contentId: existingContentId,
            storyText: content.storyText,
            audioUrl: content.audioUrl || null,
            hasMonster: content.hasMonster || false,
            monsterName: content.monsterName || null,
            terrainType: content.terrainType || terrainType,
            terrain: content.terrain || terrain,
            faction: content.faction || null,
            metadata: content.metadata
          },
          wasGenerated: false,
          cached: true,
          isRevisit: true
        };
      } else {
        console.warn(`⚠️  Narrative ${existingContentId} not found, will generate new one`);
      }
    }
    
    // STEP 2: First visit - get player's seen narratives for filtering
    console.log(`\n🆕 FIRST VISIT: Looking for available terrain narratives`);
    
    // Use provided seenNarratives if available (from PlayerContext), otherwise query Firestore
    const seenContentIds = seenNarratives 
      ? [...new Set(seenNarratives.map(n => n.contentId))] // Extract unique contentIds
      : await getPlayerSeenContentIds(userId); // Fallback to Firestore query
    
    console.log(`   Player has seen ${seenContentIds.length} unique narratives`, 
                seenNarratives ? '(from ref)' : '(from Firestore)');
    if (seenContentIds.length > 0) {
      console.log(`   Seen IDs:`, seenContentIds);
    }
    
    // STEP 3: Query available terrain narratives (exclude player's seen ones)
    const contentRef = collection(db, 'storyContent');
    const q = query(
      contentRef,
      where('terrainType', '==', terrainType)
    );
    
    const existingNarratives = await getDocs(q);
    console.log(`   Found ${existingNarratives.docs.length} total ${terrainType} narratives`);
    
    if (!existingNarratives.empty) {
      // Filter out narratives the player has already seen OR that don't have audio
      const availableNarratives = existingNarratives.docs.filter(doc => {
        const contentId = doc.id;
        const content = doc.data();
        const alreadySeen = seenContentIds.includes(contentId);
        
        if (alreadySeen) {
          console.log(`   ❌ Excluding ${contentId} (already seen)`);
          return false;
        }
        
        // Skip narratives without audio (old/incomplete data)
        if (!content.audioUrl) {
          console.log(`   ❌ Excluding ${contentId} (no audio)`);
          return false;
        }
        
        console.log(`   ✅ Available: ${contentId}`);
        return true;
      });
      
      if (availableNarratives.length > 0) {
        // Pick a random available narrative
        const randomIndex = Math.floor(Math.random() * availableNarratives.length);
        const selectedDoc = availableNarratives[randomIndex];
        const content = selectedDoc.data();
        const contentId = selectedDoc.id;
        
        console.log(`\n📦 Using cached terrain narrative: ${contentId}`);
        
        // Record this assignment for future revisits (use callback to update PlayerContext state)
        if (recordSeenNarrativeCallback) {
          recordSeenNarrativeCallback(hexKey, contentId);
        } else {
          // Fallback to direct Firestore update (backwards compatibility)
          await recordPlayerNarrative(userId, hexKey, contentId);
        }
        
        return {
          narrative: {
            contentId,
            storyText: content.storyText,
            audioUrl: content.audioUrl || null,
            hasMonster: content.hasMonster || false,
            monsterName: content.monsterName || null,
            terrainType: content.terrainType || terrainType,
            terrain: content.terrain || terrain,
            faction: content.faction || null,
            metadata: content.metadata
          },
          wasGenerated: false,
          cached: true,
          isRevisit: false
        };
      }
    }
    
    // STEP 4: Check if terrain cap is reached before generating new
    const totalNarrativesForTerrain = existingNarratives.docs.length;
    const capReached = totalNarrativesForTerrain >= TERRAIN_NARRATIVE_CAP;
    
    if (capReached) {
      console.log(`\n⚠️ Terrain cap reached for ${terrainType} (${totalNarrativesForTerrain}/${TERRAIN_NARRATIVE_CAP})`);
      console.log(`   Player has seen all available narratives, resetting seen list...`);
      
      // Get all contentIds for this terrain type
      const allContentIds = existingNarratives.docs.map(doc => doc.id);
      
      // Clear player's seen list for this terrain
      await clearSeenNarratives(userId, allContentIds);
      
      // Now try to select again from all narratives (should have options now)
      const narrativesWithAudio = existingNarratives.docs.filter(doc => {
        const content = doc.data();
        return !!content.audioUrl; // Only include narratives with audio
      });
      
      if (narrativesWithAudio.length > 0) {
        // Pick a random one
        const randomIndex = Math.floor(Math.random() * narrativesWithAudio.length);
        const selectedDoc = narrativesWithAudio[randomIndex];
        const content = selectedDoc.data();
        const contentId = selectedDoc.id;
        
        console.log(`\n🔄 Starting new cycle - selected narrative: ${contentId}`);
        
        // Record this assignment for future revisits
        if (recordSeenNarrativeCallback) {
          recordSeenNarrativeCallback(hexKey, contentId);
        } else {
          await recordPlayerNarrative(userId, hexKey, contentId);
        }
        
        return {
          narrative: {
            contentId,
            storyText: content.storyText,
            audioUrl: content.audioUrl || null,
            hasMonster: content.hasMonster || false,
            monsterName: content.monsterName || null,
            terrainType: content.terrainType || terrainType,
            terrain: content.terrain || terrain,
            faction: content.faction || null,
            metadata: content.metadata
          },
          wasGenerated: false,
          cached: true,
          isRevisit: false
        };
      }
      
      // Edge case: Cap reached but no narratives have audio (shouldn't happen)
      if (!FEATURES.GENERATE_NEW_TERRAIN_NARRATIVES) {
        console.error(`❌ Terrain generation disabled - cap reached but no audio available for ${terrainType}`);
        throw new Error(`All ${terrainType} narratives exhausted and generation is disabled. Contact admin.`);
      }
      console.log(`   ⚠️ Cap reached but no narratives with audio found, generating anyway...`);
    }
    
    // STEP 5: No available narratives - generate new one (or cap not reached)
    if (!FEATURES.GENERATE_NEW_TERRAIN_NARRATIVES) {
      console.error(`❌ Terrain generation disabled - no available narratives for ${terrainType}`);
      throw new Error(`No available narratives for ${terrainType} and generation is disabled. Contact admin.`);
    }
    
    console.log(`\n🎨 No available narratives found, generating new one...`);
    
    const generateStory = httpsCallable(functions, 'generateStorySnippet');
    const result = await generateStory({
      currentHex: hexCoord,
      terrain,
      faction,
      previousHex,
      previousTerrain,
      requestMonster,
      monsterName,
      userId, // Pass userId for player tracking
      terrainType // Pass terrainType for proper categorization
    });
    
    const data = result.data as any;
    
    console.log(`✨ Generated new ${terrainType} narrative`);
    console.log(`   Content ID: ${data.contentId}`);
    console.log(`   Has Monster: ${data.hasMonster}`);
    
    // Record this assignment for future revisits (use callback to update PlayerContext state)
    if (data.contentId) {
      if (recordSeenNarrativeCallback) {
        recordSeenNarrativeCallback(hexKey, data.contentId);
      } else {
        // Fallback to direct Firestore update (backwards compatibility)
        await recordPlayerNarrative(userId, hexKey, data.contentId);
      }
    }
    
    return {
      narrative: {
        contentId: data.contentId || 'generated',
        storyText: data.text,
        audioUrl: data.audioUrl || null,
        hasMonster: data.hasMonster || false,
        monsterName: data.monsterName || null,
        terrainType,
        terrain,
        faction,
        metadata: {
          createdAt: new Date(),
          generatedBy: userId,
          usedByPlayers: 1
        }
      },
      wasGenerated: true,
      cached: false,
      isRevisit: false
    };
    
  } catch (error) {
    console.error('Error in getOrGenerateTerrainNarrative:', error);
    throw error;
  }
}

/**
 * Get terrain type string from terrain enum
 */
export function getTerrainTypeName(terrain: number): string {
  const terrainNames: Record<number, string> = {
    0: "Mountain",
    1: "Tundra",
    2: "Hills",
    3: "Forest",
    4: "Settlement",
    5: "River",
    6: "Desert",
    7: "Ocean",
  };
  
  return terrainNames[terrain] || "Unknown";
}

